/**
 * Pure helpers behind the Admin console's Roles panel (FEA-34).
 *
 * Kept out of the component so the rules that decide whether a save is safe are
 * unit-testable. These mirror the server's validation in
 * `src/shared/auth/roleConfig.js` — the server is the real gate, and it rejects
 * every case below with a 400; doing it here too is so the console can disable
 * the button and say why, rather than letting an administrator discover a
 * self-lockout attempt by submitting it.
 */

/** A role name the platform assigns in code, so it cannot be deleted. */
export const isRequiredRole = (name, requiredRoles = []) => requiredRoles.includes(name);

/** Does this definition confer administration? */
export const isAdminCapable = (definition) => {
  const capabilities = (definition && definition.capabilities) || [];
  return capabilities.includes('*') || capabilities.includes('system:admin');
};

/** Role names in the map that can administer the platform. */
export const adminCapableRoles = (roles = {}) =>
  Object.keys(roles).filter((name) => isAdminCapable(roles[name]));

/**
 * Server-side role names are lower-case, hyphenated and 2-40 chars. Normalising
 * here means an administrator can type "Compliance Approver" and get
 * "compliance-approver" rather than a 400.
 */
export const normaliseRoleName = (input) =>
  String(input || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

export const ROLE_NAME_RE = /^[a-z][a-z0-9-]{1,39}$/;

/**
 * Why a proposed map cannot be saved, as human-readable strings. Empty means
 * the console may submit it.
 */
export function roleMapErrors(roles, requiredRoles = []) {
  const errors = [];
  const names = Object.keys(roles || {});

  if (!names.length) {
    return ['At least one role is required'];
  }

  if (!adminCapableRoles(roles).length) {
    errors.push(
      "At least one role must hold 'system:admin' — saving this would leave nobody able to reach this page"
    );
  }

  const missing = requiredRoles.filter((name) => !names.includes(name));
  if (missing.length) {
    errors.push(`These roles are assigned by the platform and cannot be removed: ${missing.join(', ')}`);
  }

  const invalid = names.filter((name) => !ROLE_NAME_RE.test(name));
  if (invalid.length) {
    errors.push(`Invalid role name(s): ${invalid.join(', ')}`);
  }

  return errors;
}

/** Toggle one capability or module on a role, returning a new map. */
export function toggleValue(roles, roleName, field, value) {
  const definition = roles[roleName];
  if (!definition) return roles;

  const current = definition[field] || [];
  const next = current.includes(value)
    ? current.filter((entry) => entry !== value)
    : [...current, value].sort();

  return { ...roles, [roleName]: { ...definition, [field]: next } };
}

/** Add an empty role, or return the map unchanged if the name is taken. */
export function addRole(roles, rawName) {
  const name = normaliseRoleName(rawName);
  if (!name || roles[name]) return roles;

  return {
    ...roles,
    [name]: { label: rawName.trim() || name, capabilities: [], modules: [] }
  };
}

export function removeRole(roles, name) {
  const next = { ...roles };
  delete next[name];
  return next;
}

export function setLabel(roles, name, label) {
  const definition = roles[name];
  if (!definition) return roles;
  return { ...roles, [name]: { ...definition, label } };
}

/**
 * A wildcard role holds every capability. The checkbox grid shows those as
 * checked-and-locked rather than pretending they are individually toggleable,
 * which would silently rewrite '*' into a list on the first click.
 */
export const hasWildcard = (definition, field) => ((definition && definition[field]) || []).includes('*');

export const isChecked = (definition, field, value) =>
  hasWildcard(definition, field) || (((definition && definition[field]) || []).includes(value));
